import DOMPurify from "dompurify";
import { marked } from "marked";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import useReaderStore from "../../context/readerStore";
import {
  useIsMobile,
  useIsTablet,
  useIsTouchTablet,
} from "../../hooks/useMediaQuery";
import { fetchPullouts } from "../../services/aiService";
import { isFeatureAllowed } from "../../services/drmPolicyService";
import {
  getPulloutId,
  PULLOUT_DISPLAY,
  selectActivePullout,
} from "../../utils/pullouts";
import "./Pullouts.css";

const PAGE_DEBOUNCE_MS = 800;
const BACKGROUND_REFRESH_MS = 5 * 60 * 1000;
const CHUNK_SIZE = 5;

marked.setOptions({ gfm: true, breaks: true });

/**
 * Wrap partner HTML in a full document for iframe srcDoc.
 * Keeps shell overrides minimal: fit 100vh stages, let reader dismiss.
 */
const wrapPulloutSrcDoc = (html) =>
  `<!DOCTYPE html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/><base target="_blank" rel="noopener noreferrer"/><style>
html,body{margin:0;padding:0;background:transparent;overflow:visible}
.stage{min-height:0!important;height:auto!important;align-items:flex-start!important;padding-block:12px!important}
button.close[aria-label="Close"],button.close[aria-label="close"]{display:none!important}
</style></head><body>${html}</body></html>`;

const sanitizeMarkdownFallback = (text) => {
  const rawText = typeof text === "string" ? text.trim() : "";
  if (!rawText) return "";
  return DOMPurify.sanitize(marked.parse(rawText), {
    USE_PROFILES: { html: true },
  });
};

const getPulloutContent = (html, text) => {
  const rawHtml = typeof html === "string" ? html.trim() : "";
  if (rawHtml) {
    return { kind: "frame", srcDoc: wrapPulloutSrcDoc(rawHtml) };
  }
  const safe = sanitizeMarkdownFallback(text);
  return safe ? { kind: "html", safeHtml: safe } : null;
};

const openPulloutUrl = (url) => {
  const href = typeof url === "string" ? url.trim() : "";
  if (href) window.open(href, "_blank", "noopener,noreferrer");
};

const getHintPreview = (pullout, emptyFallback, maxLen = 110) => {
  const raw = String(pullout?.text || pullout?.html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/[*_#>`]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!raw) return emptyFallback;
  return raw.length > maxLen ? `${raw.slice(0, maxLen).trim()}…` : raw;
};

/** Visible page range for reference_page matching (PDF/EPUB single + double). */
const useVisiblePageRange = () => {
  const currentPage = useReaderStore((s) => s.currentPage);
  const currentPageEnd = useReaderStore((s) => s.currentPageEnd);
  const documentType = useReaderStore((s) => s.documentType);
  const pageViewMode = useReaderStore((s) => s.pageViewMode);
  const totalPages = useReaderStore((s) => s.totalPages);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isTouchTablet = useIsTouchTablet();

  return useMemo(() => {
    const start = Number(currentPage) || 1;
    const allowDouble = !isMobile && !isTablet && !isTouchTablet;
    const isPdfDouble =
      documentType === "pdf" &&
      allowDouble &&
      pageViewMode === "double" &&
      start < (Number(totalPages) || start);

    if (isPdfDouble) {
      return { currentPage: start, currentPageEnd: start + 1 };
    }

    // EPUB double page sets currentPageEnd via setCurrentPageWithEnd.
    const end = Number(currentPageEnd);
    return {
      currentPage: start,
      currentPageEnd: Number.isFinite(end) && end >= start ? end : start,
    };
  }, [
    currentPage,
    currentPageEnd,
    documentType,
    pageViewMode,
    totalPages,
    isMobile,
    isTablet,
    isTouchTablet,
  ]);
};

const usePullouts = (enabled) => {
  const documentId = useReaderStore((s) => s.documentId);
  const drmPolicy = useReaderStore((s) => s.drmPolicy);
  const pulloutAllowed = isFeatureAllowed(drmPolicy, "pullout");
  const effectivelyEnabled = Boolean(enabled && pulloutAllowed);
  const { currentPage, currentPageEnd } = useVisiblePageRange();

  const [pullouts, setPullouts] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => new Set());

  const requestIdRef = useRef(0);
  const lastChunkRef = useRef(null);
  const lastDocRef = useRef(null);
  const wasEnabledRef = useRef(false);
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;

  const dismiss = useCallback((pullout) => {
    const id = getPulloutId(pullout);
    if (!id) return;
    setDismissedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const load = useCallback(
    async ({ page, action = "page_loaded" } = {}) => {
      if (!effectivelyEnabled || !documentId) return;
      const requestId = ++requestIdRef.current;
      try {
        const next = await fetchPullouts({
          document_id: documentId,
          page_number: page,
          action,
        });
        if (requestId !== requestIdRef.current) return;
        setPullouts(next);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        if (process.env.NODE_ENV === "development") {
          console.warn("[pullouts] fetch failed", error);
        }
      }
    },
    [documentId, effectivelyEnabled],
  );

  useEffect(() => {
    if (lastDocRef.current === documentId) return;
    lastDocRef.current = documentId || null;
    lastChunkRef.current = null;
    wasEnabledRef.current = false;
    setPullouts([]);
    setDismissedIds(new Set());
  }, [documentId]);

  // Drop cached pullouts when DRM disallows the feature.
  useEffect(() => {
    if (pulloutAllowed) return;
    wasEnabledRef.current = false;
    lastChunkRef.current = null;
    setPullouts([]);
  }, [pulloutAllowed]);

  useEffect(() => {
    if (!effectivelyEnabled || !documentId) {
      wasEnabledRef.current = false;
      return undefined;
    }

    const page = Number(currentPage) || 1;
    const chunk = Math.floor((page - 1) / CHUNK_SIZE);
    const justBecameReady = !wasEnabledRef.current;
    wasEnabledRef.current = true;

    if (!justBecameReady && lastChunkRef.current === chunk) return undefined;

    const isFirst = lastChunkRef.current == null || justBecameReady;
    const timer = setTimeout(
      () => {
        lastChunkRef.current = chunk;
        load({
          page,
          action: isFirst ? "page_loaded" : "chunk_loaded",
        });
      },
      isFirst ? 0 : PAGE_DEBOUNCE_MS,
    );

    return () => clearTimeout(timer);
  }, [currentPage, documentId, effectivelyEnabled, load]);

  useEffect(() => {
    if (!effectivelyEnabled || !documentId) return undefined;
    const timer = setInterval(() => {
      load({
        page: Number(currentPageRef.current) || 1,
        action: "background_refresh",
      });
    }, BACKGROUND_REFRESH_MS);
    return () => clearInterval(timer);
  }, [documentId, effectivelyEnabled, load]);

  const active = useMemo(() => {
    if (!pulloutAllowed) return null;
    return selectActivePullout(pullouts, {
      currentPage,
      currentPageEnd,
      dismissedIds,
    });
  }, [pullouts, currentPage, currentPageEnd, dismissedIds, pulloutAllowed]);

  return { active, dismiss };
};

const PulloutShell = ({ pullout, onDismiss, behindPanels = false }) => {
  const { t } = useTranslation();
  const display = String(pullout?.display || "").toLowerCase();
  const isModal = display === PULLOUT_DISPLAY.MODAL;
  const isHints = display === PULLOUT_DISPLAY.HINTS;
  const [hintExpanded, setHintExpanded] = useState(false);
  const frameRef = useRef(null);
  const frameHeightRef = useRef(0);
  const fitRafRef = useRef(0);
  const resizeObserverRef = useRef(null);

  const content = useMemo(
    () => getPulloutContent(pullout?.html, pullout?.text),
    [pullout?.html, pullout?.text],
  );

  const fitFrame = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    try {
      const doc = frame.contentDocument;
      const root = doc?.documentElement;
      const body = doc?.body;
      if (!root || !body) return;

      let height = Math.max(
        body.scrollHeight || 0,
        body.offsetHeight || 0,
        root.scrollHeight || 0,
        root.offsetHeight || 0,
      );
      for (const el of body.children) {
        height = Math.max(height, el.scrollHeight || 0, el.offsetHeight || 0);
      }

      const next = Math.ceil(height);
      // Skip no-op writes — height changes re-fire ResizeObserver.
      if (next > 0 && next !== frameHeightRef.current) {
        frameHeightRef.current = next;
        frame.style.height = `${next}px`;
      }
    } catch {
      /* cross-origin — leave default height */
    }
  }, []);

  /** Defer fit so ResizeObserver doesn't loop in the same tick. */
  const scheduleFitFrame = useCallback(() => {
    if (fitRafRef.current) cancelAnimationFrame(fitRafRef.current);
    fitRafRef.current = requestAnimationFrame(() => {
      fitRafRef.current = 0;
      fitFrame();
    });
  }, [fitFrame]);

  const onFrameLoad = useCallback(() => {
    frameHeightRef.current = 0;
    fitFrame();
    try {
      const doc = frameRef.current?.contentDocument;
      const body = doc?.body;
      doc?.querySelectorAll?.("img")?.forEach((img) => {
        if (!img.complete) {
          img.addEventListener("load", scheduleFitFrame, { once: true });
        }
      });
      resizeObserverRef.current?.disconnect?.();
      resizeObserverRef.current = null;
      if (body && typeof ResizeObserver !== "undefined") {
        const ro = new ResizeObserver(() => scheduleFitFrame());
        ro.observe(body);
        resizeObserverRef.current = ro;
      }
    } catch {
      /* ignore */
    }
  }, [fitFrame, scheduleFitFrame]);

  useEffect(() => {
    if (content?.kind !== "frame") return undefined;
    const onResize = () => scheduleFitFrame();
    window.addEventListener("resize", onResize);
    // Single delayed pass for late image/layout settle (RO + img load cover the rest).
    const timer = setTimeout(scheduleFitFrame, 300);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(timer);
      if (fitRafRef.current) cancelAnimationFrame(fitRafRef.current);
      fitRafRef.current = 0;
      resizeObserverRef.current?.disconnect?.();
      resizeObserverRef.current = null;
    };
  }, [content, scheduleFitFrame, hintExpanded]);

  if (!pullout || !content || typeof document === "undefined") return null;

  const variant = isHints
    ? `igp-pullout--hints igp-pullout--hints-${hintExpanded ? "expanded" : "collapsed"}`
    : display === PULLOUT_DISPLAY.BANNER
      ? "igp-pullout--banner"
      : "igp-pullout--modal";

  const onBodyClick = (event) => {
    if (
      event.target?.closest?.(
        "a, button, input, textarea, select, [role='button']",
      )
    ) {
      return;
    }
    if (isHints && !hintExpanded) {
      setHintExpanded(true);
      return;
    }
    openPulloutUrl(pullout.url);
  };

  const behindClass = behindPanels ? " igp-pullout--behind-panels" : "";

  const shell = (
    <div
      className={`igp-pullout ${variant}${behindClass}`}
      role={isModal ? "dialog" : undefined}
      aria-modal={isModal || undefined}
      aria-label={
        pullout.title || display || t("pullouts.fallbackTitle")
      }
    >
      <div className="igp-pullout__actions">
        {isHints ? (
          <button
            type="button"
            className="igp-pullout__toggle"
            aria-label={
              hintExpanded
                ? t("pullouts.collapseHint")
                : t("pullouts.expandHint")
            }
            aria-expanded={hintExpanded}
            onClick={(event) => {
              event.stopPropagation();
              setHintExpanded((open) => !open);
            }}
          >
            {hintExpanded ? (
              <FaChevronDown size={12} aria-hidden="true" />
            ) : (
              <FaChevronUp size={12} aria-hidden="true" />
            )}
          </button>
        ) : null}
        <button
          type="button"
          className="igp-pullout__close"
          aria-label={t("common.close")}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onDismiss?.(pullout);
          }}
        >
          <CloseIcon width={14} height={14} aria-hidden="true" />
        </button>
      </div>

      <div
        className="igp-pullout__body"
        role={isHints && !hintExpanded ? "button" : pullout.url ? "link" : undefined}
        tabIndex={isHints && !hintExpanded ? 0 : pullout.url ? 0 : undefined}
        onClick={onBodyClick}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          onBodyClick(event);
        }}
      >
        {isHints && !hintExpanded ? (
          <p className="igp-pullout-hint-preview">
            <span className="igp-pullout-hint-preview__label">
              {t("pullouts.hintLabel")}
            </span>{" "}
            {getHintPreview(pullout, t("pullouts.hintAvailable"))}
          </p>
        ) : content.kind === "frame" ? (
          <iframe
            ref={frameRef}
            className="igp-pullout-html-frame"
            title={pullout.title || t("pullouts.fallbackTitle")}
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-same-origin"
            srcDoc={content.srcDoc}
            onLoad={onFrameLoad}
          />
        ) : (
          <div
            className="igp-pullout-html"
            dangerouslySetInnerHTML={{ __html: content.safeHtml }}
          />
        )}
      </div>
    </div>
  );

  if (isModal) {
    return createPortal(
      <div
        className={`igp-pullout-overlay${behindClass}`}
        role="presentation"
      >
        {shell}
      </div>,
      document.body,
    );
  }

  return createPortal(shell, document.body);
};

/**
 * True while any reader sidebar/panel is open. Single selector so we only
 * re-render when the boolean flips (not on every panel-flag churn).
 * Used to drop pullout z-index behind panels — not unmount — so desktop still
 * shows the pullout over the book; mobile full-screen panels cover it.
 */
const useReaderPanelOpen = () =>
  useReaderStore(
    (s) =>
      s.isTocSidebarOpen ||
      s.isBookmarksSidebarOpen ||
      s.isNotesSidebarOpen ||
      s.isSearchSidebarOpen ||
      s.isPagesSidebarOpen ||
      s.isReadingAssistantSidebarOpen ||
      s.isPublicationDetailsSidebarOpen,
  );

/** PDF + EPUB host. Fixed overlay only — does not shift reader content. */
const Pullouts = ({ enabled = true }) => {
  const { active, dismiss } = usePullouts(enabled);
  const behindPanels = useReaderPanelOpen();
  if (!enabled || !active) return null;
  return (
    <PulloutShell
      key={getPulloutId(active)}
      pullout={active}
      onDismiss={dismiss}
      behindPanels={behindPanels}
    />
  );
};

export default Pullouts;
